import { useCallback, useState } from 'react';
import { useToastContext } from '@/contexts/toastContext';
import useApiSubmit from '@/hooks/useApiSubmit';
import useProfile from '@/hooks/useProfileV2';
import interviewTechnicalChallengesService from '@/services/interviewTechnicalChallenges';
import { AvailableRoles } from '@/types';
import { InterviewTechnicalChallenge } from '@/types/api';
import { hasSomeRole } from '@/utils/user';
import TechnicalValidationProjectComponent, {
  TechnicalValidationProjectComponentProps,
} from './TechnicalValidationProject.component';

type TechnicalValidationProjectProps = Omit<
  TechnicalValidationProjectComponentProps,
  'technicalDesignIsOpen' | 'onToggleTechnicalDesignModal' | 'isAdmin' | 'onUpdateGitlabUrl'
>;

export default function TechnicalValidationProject({
  technicalChallengeId,
  onRefetchInterview,
  ...props
}: TechnicalValidationProjectProps): JSX.Element {
  const [technicalDesignIsOpen, setTechnicalDesignIsOpen] = useState(false);
  const { profile } = useProfile();
  const toast = useToastContext();

  const isAdmin = hasSomeRole(profile, AvailableRoles.PEOPLE_ADMIN, AvailableRoles.HIRING_ADMIN);

  const updateChallengeMutation = useApiSubmit<
    InterviewTechnicalChallenge,
    {
      interviewTechnicalChallengeId: string;
      interviewTechnicalChallenge: Partial<InterviewTechnicalChallenge>;
    }
  >(interviewTechnicalChallengesService.editInterviewTechnicalChallenge);

  const onToggleTechnicalDesignModal = useCallback((): void => {
    setTechnicalDesignIsOpen((prevIsOpen) => !prevIsOpen);
  }, []);

  const onUpdateGitlabUrl = useCallback(
    async (newUrl: string): Promise<void> => {
      if (!technicalChallengeId) {
        toast.showErrorFeedback('Unable to update GitLab URL: Challenge ID not found.');
        return;
      }

      try {
        await updateChallengeMutation.mutateAsync({
          interviewTechnicalChallengeId: technicalChallengeId,
          interviewTechnicalChallenge: {
            gitlabProjectUrl: newUrl,
          },
        });

        toast.showSuccessFeedback('GitLab URL updated successfully.');

        if (onRefetchInterview) {
          onRefetchInterview();
        }
      } catch (error) {
        toast.showErrorFeedback('Failed to update GitLab URL. Please try again.');
      }
    },
    [technicalChallengeId, onRefetchInterview, toast, updateChallengeMutation]
  );

  return (
    <TechnicalValidationProjectComponent
      technicalDesignIsOpen={technicalDesignIsOpen}
      onToggleTechnicalDesignModal={onToggleTechnicalDesignModal}
      isAdmin={isAdmin}
      onUpdateGitlabUrl={onUpdateGitlabUrl}
      onRefetchInterview={onRefetchInterview}
      {...props}
    />
  );
}
